<template>
  <div
    id="view-events"
    class="view-events"
  >
    <LoadingIndicator v-if="loading" />
    <cms-events
      v-else-if="!!content.length"
      :options="themeSettings"
      :content="content"
      :events-config="config"
      :component-id="componentId"
      :path-prefix="pathPrefix"
      :is-preview="isThemePreview"
      :logo="watermarkLogo"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useMainStore } from '../../../stores/mainStore.js';
import { useTranslationStore } from '../../../stores/translationStore.js';
import LoadingIndicator from '../../global/LoadingIndicator.vue';
import HomepageFetchHelper from '../../../helpers/homepage-fetcher.js';

defineOptions({
  name: 'ViewEvents',
});

const props = defineProps({
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
  containerId: {
    type: String,
    default: null,
  },
});

const mainStore = useMainStore();
const translationStore = useTranslationStore();

const nuxtGlobs = { axios: null, staticFetch: null };

const loading = ref(true);
const content = ref([]);
const config = ref({});

const homepage = computed(() => mainStore.homepage);
const theme = computed(() => mainStore.theme);
const isThemePreview = computed(() => mainStore.isThemePreview);
const featureFlags = computed(() => mainStore.featureFlags);
const globals = computed(() => mainStore.globals);
const staticFilesConfig = computed(() => mainStore.staticFilesConfig);
const mediaPreviewContent = computed(() => mainStore.mediaPreviewContent);
const customSections = computed(() => mainStore.customSections);

const defaultData = computed(() => mediaPreviewContent.value?.eventsPreviewContent || []);

const themeSettings = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...theme.value.containers[props.containerIdx].components[props.componentIdx].options,
    translations: translationStore.getComponentTranslation('events'),
    globalThemeSettings: mainStore.getGlobalThemeSettings,
  };
});

const componentId = computed(() => {
  if (isThemePreview.value) {
    return 'preview_id';
  }
  return `${props.containerId}_${props.componentIdx}`;
});

const pathPrefix = computed(() => mainStore.pathPrefix);
const useS3ForMedia = computed(() => featureFlags.value.cms_stored_data_events_enabled);

const watermarkLogo = computed(() => globals.value.watermark?.url || globals.value.logo.url);

const hasMultipleEventsSections = computed(
  () => customSections.value.filter((section) => section.type === 'events').length > 1,
);

const sectionIds = computed(
  () =>
    homepage.value.events[componentId.value]?.selected_sections ||
    homepage.value.events.sectionIds[componentId.value] ||
    [],
);

async function fetchContentFromS3AndValidate() {
  try {
    const s3UrlForContent = mainStore.getS3UrlForMedia('events');
    const contentResponse = await nuxtGlobs.staticFetch.get(s3UrlForContent);

    const lastS3FileUpdate = contentResponse.data?.meta?.last_static_update;
    const lastMediaUpdate =
      staticFilesConfig.value?.last_static_update?.events ||
      staticFilesConfig.value?.homepage?.last_static_update?.events;
    const isStaticDataFresh = HomepageFetchHelper.validateLastStaticUpdate(lastS3FileUpdate, lastMediaUpdate);
    const eventsEntries = contentResponse?.data?.events || [];

    if (!isStaticDataFresh || !eventsEntries || HomepageFetchHelper.hasOutdatedEntries(eventsEntries, 'events')) {
      return await fetchContentFromThrillshare();
    }

    return filterEventsBySectionIds(eventsEntries);
  } catch (e) {
    console.error(e);
    return await fetchContentFromThrillshare();
  }
}

async function fetchContentFromThrillshare() {
  try {
    const { url, params } = mainStore.getEventsApi(nuxtGlobs.axios.defaults.baseURL);

    const contentResponse = await nuxtGlobs.axios.get(url, {
      params: {
        ...params,
        ...(sectionIds.value.length && { section_ids: sectionIds.value.join(',') }),
      },
      cache: { ignoreCache: false },
    });
    return contentResponse.data?.events || [];
  } catch (error) {
    console.error(error);
    return [];
  }
}

function filterEventsBySectionIds(events) {
  if (sectionIds.value.length) {
    return events.filter((event) => sectionIds.value.includes(event.custom_section_id));
  }
  return events;
}

function setupThemePreview() {
  content.value = defaultData.value;
  loading.value = false;
}

async function getContent() {
  if (isThemePreview.value) {
    setupThemePreview();
    return;
  }

  if (useS3ForMedia.value) {
    content.value = await fetchContentFromS3AndValidate();
  } else {
    content.value = await fetchContentFromThrillshare();
  }
  loading.value = false;
}

onMounted(() => {
  const inst = getCurrentInstance();
  const p = inst?.proxy;
  const gp = inst?.appContext.config.globalProperties;
  nuxtGlobs.axios = p?.$axios ?? gp?.$axios;
  nuxtGlobs.staticFetch = p?.$staticFetch ?? gp?.$staticFetch;
  config.value = {
    ...homepage.value.events,
    display_tags: homepage.value?.events?.display_tags || hasMultipleEventsSections.value,
  };
  getContent();
});
</script>

<style scoped lang="scss">
.view-events {
  height: 100%;
}
</style>
